import { isMagicbuyProject } from './projectUtils'

export const DAMAGE_INSURANCE_RATE = 0.05
export const DAMAGE_INSURANCE_MIN_CLAIM_AMOUNT = 500
export const DAMAGE_INSURANCE_MIN_PREMIUM = 25
export const DAMAGE_INSURANCE_SEA_MIN_CLAIM_AMOUNT = 2000
export const DAMAGE_INSURANCE_SEA_MIN_PREMIUM = 200
export const DAMAGE_INSURANCE_MAX_CLAIM_AMOUNT = 1000000
export const DAMAGE_INSURANCE_AGREEMENT_POST_ID = '652998'
export const MGB_DAMAGE_INSURANCE_AGREEMENT_POST_ID = '1636264'
export const DAMAGE_INSURANCE_EXCLUDED_WAREHOUSE_ID = '1598'
export const DAMAGE_INSURANCE_BRIEF_OPTIONS = Object.freeze([
  '問題件拿出，其他件寄回',
  '沒關係，壞了也寄回',
])
export const DEFAULT_DAMAGE_INSURANCE_BRIEF = ''
export const ORDER_CREATE_DAMAGE_INSURANCE_ENABLED = true
export const DAMAGE_INSURANCE_DENY_FURNITURE_SE = 'ParcelTDIFurnitureSe'
export const DAMAGE_INSURANCE_DENY_NEED_OOBA = 'ParcelTDINeedOoba'
const DAMAGE_INSURANCE_DENY_MESSAGES = Object.freeze({
  [DAMAGE_INSURANCE_DENY_FURNITURE_SE]: '此家具包裹請選擇海運，才能加購運損保價。',
  [DAMAGE_INSURANCE_DENY_NEED_OOBA]: '正式報關包裹須先完成開箱驗貨，才能加購運損保價。',
})
const DAMAGE_INSURANCE_DENY_FALLBACK_MESSAGE = '此包裹暫不可加購運損保價。'
const FORMAL_CUSTOMS_SHP_METHOD = '91'

export function getDamageInsuranceAgreementPostId (project) {
  return isMagicbuyProject(project)
    ? MGB_DAMAGE_INSURANCE_AGREEMENT_POST_ID
    : DAMAGE_INSURANCE_AGREEMENT_POST_ID
}

export function getDamageInsuranceAgreementTitle (project) {
  return `${isMagicbuyProject(project) ? '買即可' : '巧巧郎'}運損保價協議`
}

function parcelExtraInfo (parcel) {
  return parcel?.extraInfo || {}
}

export function getDamageInsurancePricingPolicy (transport) {
  if (transport === 'sea') {
    return {
      minClaimAmount: DAMAGE_INSURANCE_SEA_MIN_CLAIM_AMOUNT,
      minPremium: DAMAGE_INSURANCE_SEA_MIN_PREMIUM,
      rate: DAMAGE_INSURANCE_RATE,
    }
  }
  return {
    minClaimAmount: DAMAGE_INSURANCE_MIN_CLAIM_AMOUNT,
    minPremium: DAMAGE_INSURANCE_MIN_PREMIUM,
    rate: DAMAGE_INSURANCE_RATE,
  }
}

export function normalizeDamageInsuranceClaimAmount (value, transport) {
  const { minClaimAmount } = getDamageInsurancePricingPolicy(transport)
  const amount = Number(value)
  if (!Number.isFinite(amount) || amount < minClaimAmount) {
    return minClaimAmount
  }
  return Math.min(Math.floor(amount), DAMAGE_INSURANCE_MAX_CLAIM_AMOUNT)
}

export function calculateDamageInsurancePremium (claimAmount, transport) {
  const amount = Number(claimAmount)
  if (!Number.isFinite(amount) || amount <= 0) return 0
  const { minPremium, rate } = getDamageInsurancePricingPolicy(transport)
  return Math.max(minPremium, Math.ceil(amount * rate))
}

export function normalizeDamageInsuranceBrief (value) {
  return DAMAGE_INSURANCE_BRIEF_OPTIONS.includes(value)
    ? value
    : DEFAULT_DAMAGE_INSURANCE_BRIEF
}

// brand-fence:start id=kkren-order-damage-insurance-warehouses brand=kkren predicate=data-driven
export function isDamageInsuranceWarehouseParcel (parcel) {
  const warehouseId = String(parcel?.jyAddrid || '')
  return Boolean(warehouseId) && warehouseId !== DAMAGE_INSURANCE_EXCLUDED_WAREHOUSE_ID
}

export function isOrderCreateDamageInsuranceEnabled ({
  project,
  parcels,
} = {}) {
  return ORDER_CREATE_DAMAGE_INSURANCE_ENABLED &&
    !isMagicbuyProject(project) &&
    Array.isArray(parcels) &&
    parcels.some(isDamageInsuranceWarehouseParcel)
}
// brand-fence:end id=kkren-order-damage-insurance-warehouses

export function hasDamageInsurance (parcel) {
  return Number(parcelExtraInfo(parcel).vaTDIPremium || 0) > 0
}

export function hasClosedDamageInsurance (parcel) {
  const extraInfo = parcelExtraInfo(parcel)
  return !hasDamageInsurance(parcel) &&
    Array.isArray(extraInfo.snapshotsTDIPremium) &&
    extraInfo.snapshotsTDIPremium.length > 0
}

export function canPurchaseDamageInsurance (parcel) {
  if (
    !isDamageInsuranceWarehouseParcel(parcel) ||
    hasDamageInsurance(parcel) ||
    hasClosedDamageInsurance(parcel)
  ) {
    return false
  }
  const extraInfo = parcelExtraInfo(parcel)
  const isStorage = typeof parcel.isStorage === 'boolean'
    ? parcel.isStorage
    : Boolean(parcel.jyStorageAt && parcel.jyStorageAt !== '0000-00-00 00:00:00')
  if (!isStorage || parcel.status === 'packed') return false
  if (Number(extraInfo.hasLgFragile) === 1) return false
  if (parcel.hasWooden && !(extraInfo.snapshotsTDIPremium || []).length) return false
  if (String(parcel.splitpri || '0') !== '0') return false
  return Number(parcel.weight || 0) <= 60000
}

// 下单逐件加购限制：prepare 包裹 extraInfo.vaTDIRule.deny 按运输方式取键，正式報關（91）加 G1；
// 海運不提供正式報關，残留的 91 不参与判断。空字符串表示可加购，缺少规则按可加购处理。
export function getDamageInsuranceDenyCode (parcel, { transport, jyoShpMethod } = {}) {
  const deny = parcelExtraInfo(parcel).vaTDIRule?.deny
  if (!deny || !transport) return ''
  const isFormal = transport !== 'sea' && String(jyoShpMethod || '') === FORMAL_CUSTOMS_SHP_METHOD
  const code = deny[isFormal ? `${transport}G1` : transport] || ''
  // 家具海快与正式報關未验货同时成立时，后端返回验货原因；提示以家具海快优先。
  if (code && deny[transport] === DAMAGE_INSURANCE_DENY_FURNITURE_SE) {
    return DAMAGE_INSURANCE_DENY_FURNITURE_SE
  }
  return code
}

export function getDamageInsuranceDenyMessage (parcel, context) {
  const code = getDamageInsuranceDenyCode(parcel, context)
  if (!code) return ''
  return DAMAGE_INSURANCE_DENY_MESSAGES[code] ||
    parcelExtraInfo(parcel).vaTDIRule?.denyMsgs?.[code] ||
    DAMAGE_INSURANCE_DENY_FALLBACK_MESSAGE
}

function isSelectedDamageInsurancePurchase (parcel, selections, context) {
  return Boolean(selections?.[String(parcel.pclid)]) &&
    canPurchaseDamageInsurance(parcel) &&
    !getDamageInsuranceDenyCode(parcel, context)
}

export function getOrderCreateDamageInsuranceParcels (parcels) {
  if (!Array.isArray(parcels)) return []
  return parcels.filter(parcel => {
    return isDamageInsuranceWarehouseParcel(parcel) &&
      (hasDamageInsurance(parcel) || canPurchaseDamageInsurance(parcel))
  })
}

export function hasOrderCreateDamageInsuranceCoverage (parcels, selections = {}, context) {
  return getOrderCreateDamageInsuranceParcels(parcels).some(parcel => {
    return hasDamageInsurance(parcel) || isSelectedDamageInsurancePurchase(parcel, selections, context)
  })
}

// 受限包裹无法加购，同样属于未保价，仍需客人确认风险。
export function requiresDamageInsuranceRiskAcceptance (parcels, selections = {}, context) {
  if (!Array.isArray(parcels)) return false
  return parcels.some(parcel => {
    return canPurchaseDamageInsurance(parcel) &&
      !isSelectedDamageInsurancePurchase(parcel, selections, context)
  })
}

export function hasSelectedDamageInsurancePurchases (parcels, selections = {}, context) {
  if (!Array.isArray(parcels)) return false
  return parcels.some(parcel => isSelectedDamageInsurancePurchase(parcel, selections, context))
}

export function totalSelectedDamageInsurancePremium (parcels, selections = {}, transport, jyoShpMethod) {
  if (!Array.isArray(parcels)) return 0
  return parcels.reduce((total, parcel) => {
    const selection = selections[String(parcel.pclid)]
    if (!isSelectedDamageInsurancePurchase(parcel, selections, { transport, jyoShpMethod })) return total
    const claimAmount = normalizeDamageInsuranceClaimAmount(selection.claimAmount, transport)
    return total + calculateDamageInsurancePremium(claimAmount, transport)
  }, 0)
}

export function createDamageInsuranceSelection (parcel, claimAmount, transport) {
  const normalizedClaimAmount = normalizeDamageInsuranceClaimAmount(claimAmount, transport)
  return {
    pclid: parcel.pclid,
    claimAmount: normalizedClaimAmount,
    premium: calculateDamageInsurancePremium(normalizedClaimAmount, transport),
  }
}
